import { useTranslation } from "react-i18next"
import { ChangeEvent, MouseEvent, useState } from "react"
import { useStore } from "../../../contexts/store"
import { Tag } from "../../../shared/src/types/Content"
import { Op_addTag, Op_editTag } from "../../../shared/src/types/Ops"
import { notTagNameRegex } from "../../../shared/src/validation/validationConstantsString"

import DialogHeader from "../../Elements/DialogHeader"
import UxIcon from "../../Elements/UxIcon"

import { Box, Button, Dialog, DialogActions, DialogContent, IconButton, TextField } from "@mui/material"
import dayjs from "dayjs"


const TagSettingsDialog = ({podId, tag, tagNames}: {podId: string, tag: Tag|null, tagNames: string[]}) => {
  const { t } = useTranslation()
  const { opStore, podStore, uiStore, sessionStore } = useStore()
  const [showDialog, setShowDialog] = useState(false)
  const [name, setName] = useState<string>("")
  const deviceWidth = uiStore.deviceWidth
  const isFullscreenMode = deviceWidth === "small" || deviceWidth === "smaller" || deviceWidth === "tiny"

  function handleOpen(event: MouseEvent) {
    // set tag name in dialog
    if(tag) setName(tag.name)
    else setName("")
    // open dialog
    setShowDialog(true)
    event.stopPropagation()
  }

  function handleClose() {
    setShowDialog(false)
    setName("")
  }

  function handleSave() {
    if(name && validName() && sessionStore.session) {
      const userId = sessionStore.session.user.userId
      // edit existing tag
      if(tag) {
        const tagId = tag.tagId
        opStore.doOp({
          op: "editTag",
          podId: podId,
          data: {
            usergroupId: podStore.getUsergroupByRole('Pod').usergroupId,
            userId: userId,
            userName: podStore.userPseudonym,
            tagId: tagId,
            mods: {
              name: name,
              tModified: dayjs().unix()
            }
          }
        } as Op_editTag)
      }
      // create new tag
      else {
        const tagId = sessionStore.createUuid()
        opStore.doOp({
          op: "addTag",
          podId: podId,
          data: {
            usergroupId: podStore.getUsergroupByRole('Pod').usergroupId,
            userId: userId,
            userName: podStore.userPseudonym,
            tagId: tagId,
            name: name,
            description: "",
            coid: null,
            tCreated: dayjs().unix(),
            tModified: dayjs().unix()
          }
        } as Op_addTag)
      }
      handleClose()
    }
  }

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newName = event.target ? event.target.value : null
    if(newName !== null) {
      setName(newName.replace(notTagNameRegex,''))
    }
  }

  const validName =() => {
    if(tag && tagNames.includes(name) && name !== tag.name) return false
    if(tag === null && tagNames.includes(name)) return false
    return true
  }

  return (
    <>
    {tag === null ?
      <Box sx={{display: "flex", justifyContent: "end", padding: "20px 30px 0 30px"}}>
        <Button onClick={handleOpen} variant="contained">{t('Add Tag')}</Button>
      </Box>
      :
      <IconButton sx={{padding: "10px"}} size="small" onClick={handleOpen}>
        <UxIcon fontSize="small" id="settings" />
      </IconButton>
    }
      <Dialog
        disableRestoreFocus
        fullScreen={isFullscreenMode ? true : false}
        fullWidth
        maxWidth="md"
        onClick={(e) => {e.stopPropagation()}}
        onClose={handleClose}
        open={showDialog}
        slotProps={{
          paper: {
            sx: {
              borderRadius: isFullscreenMode ? "" : "16px"
            }
          }
        }}
      >
        <DialogHeader
          backgroundColor="primary.main"
          handleClose={handleClose}
          iconId={"podTags"}
          label={tag === null ? t('Add Tag') : t('Edit Tag')}
        />
        <DialogContent sx={{margin: "10px 0"}}>
          <Box  sx={{
            display: "grid",
            gap: "10px",
            maxWidth: "700px",
            justifySelf: "center",
            width: "100%"
          }}>
            <TextField
              autoFocus
              error={(!name || !validName()) ? true : false}
              helperText={!name ? t('The field must not be empty') : (!validName() ? t('Tag name already exists, please choose another name') : '')}
              fullWidth
              onChange={handleNameChange}
              label={t('Name')}
              required
              value={name}
              variant="outlined"
              onKeyDown={(ev) => {
                if(ev.key === 'Enter') {
                  handleSave()
                  ev.preventDefault()
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{
          display: "flex",
          gap: "10px",
          justifyItems: "end",
          padding: "20px 25px"
        }}>
          <Button onClick={handleClose}>
            {t('Cancel')}
          </Button>
          <Button variant="contained" onClick={handleSave}>
            {t('Save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default TagSettingsDialog
